import { trpc } from './trpc';
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server';
import { AppRouter } from '@tagadapay/trpc-types';
import { useToast } from '@/components/ui/use-toast';

type OutputRouter = inferRouterOutputs<AppRouter>;
type InputRouter = inferRouterInputs<AppRouter>;

// Output types
export type IntegrationListOutput = OutputRouter['integrations']['list'];
export type Integration = IntegrationListOutput[number];
export type IntegrationDetails = OutputRouter['integrations']['getById'];
export type SendCloudIntegration = OutputRouter['integrations']['getSendCloud'];
export type SendCloudCredentials = OutputRouter['integrations']['getSendCloudCredentials'];
export type DeleteIntegrationOutput = OutputRouter['integrations']['delete'];
export type RequestIntegrationOutput = OutputRouter['integrations']['requestNewIntegration'];

// Input types
export type ListIntegrationsInput = InputRouter['integrations']['list'];
export type GetIntegrationInput = InputRouter['integrations']['getById'];
export type UpdateIntegrationInput = InputRouter['integrations']['update'];
export type DeleteIntegrationInput = InputRouter['integrations']['delete'];
export type RequestIntegrationInput = InputRouter['integrations']['requestNewIntegration'];
export type GetSendCloudInput = InputRouter['integrations']['getSendCloud'];
export type GetSendCloudCredentialsInput = InputRouter['integrations']['getSendCloudCredentials'];

// List integrations for a store
export const useIntegrationsList = (storeId: string, options?: { refetchInterval?: number }) => {
  return trpc.integrations.list.useQuery(
    { storeId },
    {
      enabled: !!storeId,
      staleTime: 30000, // 30 seconds
      refetchInterval: options?.refetchInterval,
    },
  );
};

// Get single integration by ID
export const useIntegration = (id: string) => {
  return trpc.integrations.getById.useQuery(
    { integrationId: id },
    {
      enabled: !!id,
      staleTime: 30000, // 30 seconds
    },
  );
};

// Alias for backward compatibility
export const useIntegrationDetails = useIntegration;

// Get SendCloud integration (public)
export const useSendCloudIntegration = (storeId: string, enabled = true) => {
  return trpc.integrations.getSendCloud.useQuery({ storeId }, { enabled: enabled && !!storeId });
};

// Get SendCloud credentials (public)
export const useSendCloudCredentials = (storeId: string, enabled = true) => {
  return trpc.integrations.getSendCloudCredentials.useQuery({ storeId }, { enabled: enabled && !!storeId });
};

// Create or update integration (upsert)
export const useUpsertIntegration = () => {
  const { toast } = useToast();
  const utils = trpc.useUtils();

  return trpc.integrations.update.useMutation({
    onSuccess: (data) => {
      // Invalidate list query
      utils.integrations.list.invalidate();
      // Refresh the Shopify health probe so a stale red banner clears
      // as soon as the merchant saves fresh credentials.
      utils.integrations.checkShopifyCredentials.invalidate();
      utils.integrations.checkWoocommercePermissions.invalidate();
      // Update cache for single integration
      utils.integrations.getById.setData({ integrationId: data.id }, data);
      toast({
        title: 'Success',
        description: 'Integration updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Alias for backward compatibility
export const useUpdateIntegration = useUpsertIntegration;

// Delete integration
export const useDeleteIntegration = () => {
  const { toast } = useToast();
  const utils = trpc.useUtils();

  return trpc.integrations.delete.useMutation({
    onSuccess: () => {
      // Invalidate all lists and remove from cache
      utils.integrations.list.invalidate();
      utils.integrations.getById.invalidate();
      toast({
        title: 'Success',
        description: 'Integration deleted successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Request new integration via Slack
export const useRequestIntegration = () => {
  const { toast } = useToast();

  return trpc.integrations.requestNewIntegration.useMutation({
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Integration request sent successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// ============================================================================
// Apple Pay Functions
// ============================================================================

// List custom domains
export const useListCustomDomains = () => {
  return trpc.integrations.listCustomDomains.useQuery(undefined, {
    staleTime: 30000, // 30 seconds
  });
};

// Verify Apple Pay domain
export const useVerifyApplePayDomain = () => {
  const { toast } = useToast();
  const utils = trpc.useUtils();

  return trpc.integrations.verifyApplePayDomain.useMutation({
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Domain verified with Apple',
      });
      // Invalidate domains list to refetch
      utils.integrations.listCustomDomains.invalidate();
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Disconnect Apple Pay domain
export const useDisconnectApplePayDomain = () => {
  const { toast } = useToast();
  const utils = trpc.useUtils();

  return trpc.integrations.disconnectApplePayDomain.useMutation({
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Domain disconnected successfully',
      });
      // Invalidate domains list to refetch
      utils.integrations.listCustomDomains.invalidate();
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// ============================================================================
// Webhook Functions (tRPC Hooks)
// ============================================================================

/**
 * Get webhook event types
 */
export const useWebhookEventTypes = () => {
  return trpc.integrations.getWebhookEventTypes.useQuery(
    {},
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );
};

/**
 * Get webhook endpoints for a store
 */
export const useWebhookEndpoints = (storeId: string, enabled = true) => {
  return trpc.integrations.getWebhookEndpoints.useQuery(
    { storeId },
    {
      enabled: enabled && !!storeId,
      staleTime: 30000, // 30 seconds
    },
  );
};

/**
 * Get delivery attempts for a webhook endpoint
 */
export const useWebhookDeliveries = (webhookEndpointId: string, enabled = true) => {
  return trpc.integrations.getWebhookDeliveries.useQuery(
    { webhookEndpointId },
    {
      enabled: enabled && !!webhookEndpointId,
      staleTime: 15000, // 15 seconds
    },
  );
};

/**
 * Replay a failed webhook delivery by event id
 */
export const useReplayWebhookDelivery = () => {
  const utils = trpc.useUtils();
  return trpc.integrations.replayWebhookDelivery.useMutation({
    onSuccess: (_data, variables) => {
      void utils.integrations.getWebhookDeliveries.invalidate({
        webhookEndpointId: variables.webhookEndpointId,
      });
    },
  });
};

/**
 * Get the latest delivery status of every webhook endpoint in a store
 */
export const useWebhookEndpointsDeliveryStatus = (storeId: string, enabled = true) => {
  return trpc.integrations.getWebhookEndpointsDeliveryStatus.useQuery(
    { storeId },
    {
      enabled: enabled && !!storeId,
      staleTime: 30000, // 30 seconds
    },
  );
};

/**
 * Get a single webhook endpoint
 */
export const useWebhookEndpoint = (id: string, enabled = true) => {
  return trpc.integrations.getWebhookEndpoint.useQuery(
    { id },
    {
      enabled: enabled && !!id,
      staleTime: 30000, // 30 seconds
    },
  );
};

/**
 * Create a webhook endpoint
 */
export const useCreateWebhookEndpoint = () => {
  const { toast } = useToast();
  const utils = trpc.useUtils();

  return trpc.integrations.createWebhookEndpoint.useMutation({
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Webhook created successfully',
      });
      // Invalidate webhook endpoints list
      utils.integrations.getWebhookEndpoints.invalidate();
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Update a webhook endpoint
 */
export const useUpdateWebhookEndpoint = () => {
  const { toast } = useToast();
  const utils = trpc.useUtils();

  return trpc.integrations.updateWebhookEndpoint.useMutation({
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Webhook updated successfully',
      });
      // Invalidate webhook endpoints list
      utils.integrations.getWebhookEndpoints.invalidate();
      utils.integrations.getWebhookEndpoint.invalidate();
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Delete a webhook endpoint
 */
export const useDeleteWebhookEndpoint = () => {
  const { toast } = useToast();
  const utils = trpc.useUtils();

  return trpc.integrations.deleteWebhookEndpoint.useMutation({
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Webhook deleted successfully',
      });
      // Invalidate webhook endpoints list
      utils.integrations.getWebhookEndpoints.invalidate();
      utils.integrations.getWebhookEndpoint.invalidate();
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Test a webhook endpoint (optionally with a specific event type)
 */
export const useTestWebhookEndpoint = () => {
  const { toast } = useToast();

  return trpc.integrations.testWebhookEndpoint.useMutation({
    onSuccess: (data) => {
      const eventLabel =
        data?.eventType && data.eventType !== 'test.webhook' ? data.eventType : 'generic test';
      toast({
        title: 'Test sent',
        description: `"${eventLabel}" webhook delivered`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useCreateWoocommerceWebhooks = () => {
  const { toast } = useToast();
  const utils = trpc.useUtils();

  return trpc.integrations.createWoocommerceWebhooks.useMutation({
    onSuccess: (data) => {
      utils.integrations.getById.setData({ integrationId: data.id }, data);
      utils.integrations.list.invalidate({ storeId: data.storeId });
      toast({
        title: 'Success',
        description: 'WooCommerce webhooks created successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useCheckWoocommercePermissions = (storeId: string, enabled = true) => {
  return trpc.integrations.checkWoocommercePermissions.useQuery(
    { storeId },
    {
      enabled: enabled && !!storeId,
      staleTime: 5 * 60 * 1000,
      retry: false,
    },
  );
};

/** The health the daily probe keeps for the store's Shopify connection: a read, never a call to Shopify. */
export const useShopifyConnectionHealth = (storeId: string) => {
  return trpc.integrations.getShopifyConnectionHealth.useQuery(
    { storeId },
    { enabled: !!storeId, staleTime: 60 * 1000, retry: false },
  );
};

export type ShopifyConnectionHealth = NonNullable<OutputRouter['integrations']['getShopifyConnectionHealth']>;

export const useManualConnectWoocommerce = () => {
  const { toast } = useToast();
  const utils = trpc.useUtils();

  return trpc.integrations.manualConnectWoocommerce.useMutation({
    onSuccess: () => {
      utils.integrations.list.invalidate();
      toast({
        title: 'Success',
        description: 'WooCommerce store connected successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useCheckWooCommercePermalinks = () => {
  const utils = trpc.useUtils();

  return {
    checkPermalinks: async (url: string) => {
      return await utils.integrations.checkWooCommercePermalinks.fetch({ url });
    },
  };
};

// ============================================================================
// ConvesioPay
// ============================================================================

export const useConvesioConnect = () => trpc.integrations.convesioConnect.useMutation();

// ============================================================================
// Airwallex APM Functions
// ============================================================================

/**
 * Fetch available Airwallex payment methods
 */
export const useAirwallexPaymentMethods = (
  processorId: string,
  country?: string,
  currency?: string,
  enabled = true,
) => {
  return trpc.integrations.getAirwallexPaymentMethods.useQuery(
    { processorId, country, currency },
    { enabled: enabled && !!processorId },
  );
};
